import {
    ChangeDetectorRef, OnDestroy, Pipe, PipeTransform, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DestroyRef } from "@angular/core";

import { UserRequestService } from "../../iapi-application/services/request/user/user-request.service";
import { COMMON_ERROR_HANDLER } from "../../exceptions/constants/exception-constants";
import {
    Subject, takeUntil
} from "rxjs";

/**
 * This pipe converts a user id into a user name
 * i.e. {{ userId | userName }}
 *
 * It follows the async pipe pattern, and as such should never be provided or injected into a component, directive, service, etc.
 *
 * If you need to replicate the behavior outside of an html template use UserRequestService.getUserName
 */
@Pipe({
    name: "mgdUserName",
    // Pure must be false in order for the async to work correctly
    // This means that transform will be checked on each change detection cycle
    pure: false
})
export class UserNamePipe implements PipeTransform, OnDestroy {

    /**
     * Value displayed for a user that does not exist, of if the service is unable to retrieve the user
     */
    static readonly UNKNOWN_USER_NAME = "Unknown User";

    /**
     * The last userId passed into the transform function
     */
    private _lastUserId: string;

    /**
     * The last value emitted by the transform function
     */
    private _lastValue: string;

    /**
     * This subject will fire if we need to do a new lookup after
     */
    private readonly _stopLookup$ = new Subject();

    private readonly _userService = inject(UserRequestService);
    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);
    private readonly _cdRef = inject(ChangeDetectorRef);
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * This will return the user name for the given id
     *
     * If the user lookup does not return right away it will return undefined, and trigger change detection
     * when the user lookup returns
     *
     * If the user is not found, or there is a server error it will return Unknown User
     */
    transform(userId: string): string {

        if (!userId) {
            return UserNamePipe.UNKNOWN_USER_NAME;
        }

        if (userId !== this._lastUserId) {
            // The first call, and any call where the userId is changed
            // We will setup a new subscription

            this._lastValue = undefined;
            // This will stop any lookup currently running
            this._stopLookup$.next(undefined);
            this._lastUserId = userId;
            this._userService.getUserName$(userId).pipe(
                takeUntilDestroyed(this._destroyRef),
                takeUntil(this._stopLookup$)
            )
                .subscribe({
                    next: (userName) => {
                        this._lastValue = userName;
                        // This will cause the component to call the transform function again
                        // The _lastValue will then be returned
                        this._cdRef.markForCheck();
                    },
                    error: (error: unknown) => {

                        this._errorHandler.handlePipeError(error, `UserNamePipe: Failed to retrieve user name for ${userId}`);
                        this._lastValue = UserNamePipe.UNKNOWN_USER_NAME;
                        // This will cause the component to call the transform function again
                        // The _lastValue will then be returned
                        this._cdRef.markForCheck();
                    }
                });
        }
        return this._lastValue;
    }

    /**
     * Angular onDestroy lifecycle event. Complete the _stopLookup$ subject
     */
    ngOnDestroy(): void {
        this._stopLookup$.complete();
    }

}
